import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPKR } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

function configLine(c) {
  if (!c) return "";
  const parts = [];
  if (c.bedding_type) parts.push(c.bedding_type);
  if (c.size) parts.push(c.size);
  if (c.pc_count) parts.push(`${c.pc_count} pc Set`);
  if (c.pillow_count) parts.push(`${c.pillow_count} Pillows`);
  if (c.curtain_size) parts.push(c.curtain_size);
  return parts.join(" · ");
}

export default function OrderConfirmation() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="text-center py-24 text-ink-soft">Loading…</div>;
  if (!order)
    return (
      <div className="text-center py-24" data-testid="order-not-found">
        <h2 className="font-display text-3xl text-ink">Order not found</h2>
        <Link to="/" className="inline-block mt-6 text-sage uppercase tracking-widest text-xs">Back to Home</Link>
      </div>
    );

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 sm:py-16" data-testid="order-confirmation">
      <div className="text-center mb-8 sm:mb-10">
        <CheckCircle2 size={38} className="text-sage mx-auto mb-3 sm:mb-4" />
        <div className="label-eyebrow !text-[10px] sm:!text-xs mb-2">Order Confirmed</div>
        <h1 className="font-display text-[1.9rem] leading-tight sm:text-5xl text-ink">Thank you, {order.customer.full_name.split(" ")[0]}.</h1>
        <p className="text-ink-soft text-sm sm:text-base mt-3 max-w-md mx-auto">
          Order <span className="font-semibold text-ink">{order.order_number}</span> is in our hands. We'll call you to confirm delivery.
        </p>
      </div>

      <div className="bg-cream-warm border border-border-subtle p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
          <div>
            <div className="label-eyebrow !text-[10px] mb-1">Delivery To</div>
            <div className="text-ink font-semibold">{order.customer.full_name}</div>
            <div className="text-sm text-ink-soft">
              {order.customer.address_line1}{order.customer.address_line2 ? `, ${order.customer.address_line2}` : ""}<br />
              {order.customer.city}, {order.customer.province} {order.customer.postal_code}<br />
              <a href={`tel:${order.customer.phone}`} className="hover:text-sage">{order.customer.phone}</a>
            </div>
          </div>
          <div className="sm:text-right">
            <div className="label-eyebrow !text-[10px] mb-1">Payment</div>
            <div className="text-ink font-semibold">Cash on Delivery</div>
            <div className="text-sm text-ink-soft uppercase tracking-widest mt-1">{order.status}</div>
          </div>
        </div>

        <ul className="divide-y divide-border-subtle border-y border-border-subtle">
          {order.items.map((it, idx) => (
            <li key={idx} className="py-4 flex justify-between gap-4">
              <div>
                <div className="font-semibold text-ink">{it.name}</div>
                <div className="text-xs text-ink-soft">{configLine(it.config)} · ×{it.quantity}</div>
              </div>
              <div className="text-ink">{formatPKR(it.price * it.quantity)}</div>
            </li>
          ))}
        </ul>

        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between text-ink-soft"><span>Subtotal</span><span>{formatPKR(order.subtotal)}</span></div>
          <div className="flex justify-between text-ink-soft"><span>Shipping</span><span>{formatPKR(order.shipping)}</span></div>
          <div className="flex justify-between font-display text-2xl text-ink pt-2 border-t border-ink/15"><span>Total</span><span>{formatPKR(order.total)}</span></div>
        </div>
      </div>

      <div className="text-center mt-10">
        <Link
          to="/"
          className="inline-block bg-sage hover:bg-sage-dark text-cream px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
          data-testid="confirmation-continue-shopping"
        >
          Continue Shopping
        </Link>
      </div>
    </div>
  );
}
